import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape TRP-01 publishes, imported rather than restated. A column here that the service
// stopped returning is a build failure, not a blank cell.
import type { Trip } from "@tdelivr/api-client/generated/trip/trip.js";

import {
  cancelTrip,
  cancellable,
  CANCEL_SCOPE,
  rideDetail,
  boundedNote,
  readableStatus,
  riderCell,
  ridePage,
  rowTint,
  FILTERABLE_STATUSES,
  type RideOrder,
  type TripStatus,
} from "../active-rides.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Active rides: every trip currently running.
 *
 * Built to `19 WP18 Dispatch Operations.dc.html`, page `trips`. Every measurement is the one that
 * file computes for its own template, read from the rendered page rather than from a screenshot:
 * the page body is a 16px-gap grid padded `20px 22px 28px`; the tile grid is four
 * `minmax(0, 1fr)` tracks 12px apart, each tile 14px radius with `14px 16px` of padding, a 12.5px
 * label, a 26px count at `-0.02em` and a pill; the data grid is a 16px-radius card holding a
 * `12px 14px` toolbar, a `10px 16px` header strip on `--sunken`, `12px 16px` rows and a
 * `10px 14px` pager; the grid's tracks are its own `minmax(0, 1.5fr)` then one `minmax(0, 1fr)` per
 * remaining column then `190px`, 14px apart; and the closing note is a 14px-radius `--t-sky`
 * callout with 20px of glyph and 13.5/20 text.
 *
 * **One service, read directly.** TRP-01's `GET /v1/tenants/{tenantId}/trips` is the operation whose
 * own description calls itself this board, and every column the board can draw is on the row it
 * answers — so this screen talks to the service through the console's transport, as the demand
 * queue and the offer monitor do, and no aggregate exists for it. Its delivery twin needs one, and
 * `ActiveDeliveries.tsx` says why.
 *
 * **Two of the design's three data columns and one of its four tiles are built.** `Trip` is
 * `tripId`; `Rider` is `riderId`, because no operation on this platform turns a rider identifier
 * into a name; `State` is `status`. The third column draws a route — `Westlands → JKIA · 3 stops` —
 * and there is no geocoder on this platform and no origin or destination on a trip row at all. The
 * tiles for completed, cancelled and disputed trips need a date-bounded total that no listing
 * publishes, and `disputed` is not a trip status anywhere. R-19 in
 * `docs/design/DESIGN_RECONCILIATION.md` is the measurement for each, and the columns and tiles are
 * absent rather than filled: a column of plausible numbers is worse than a column that is not
 * there, because an operator would work it.
 *
 * The grid's own tracks follow from that. The design derives them from its column list —
 * `minmax(0, 1.5fr)` for the first, `minmax(0, 1fr)` for each of the rest and a fixed 190px for the
 * state cell — so a board with two sourced columns is `minmax(0, 1.5fr) minmax(0, 1fr) 190px` by
 * the design's own rule rather than by a new one.
 */

const trips = services.trip__trip;

/**
 * How many rows to take.
 *
 * 200 is TRP-01's own maximum `limit`, and its contract says why: "more than any board displays,
 * and small enough that a page is answered from the index without a sort. A caller wanting more is
 * exporting, which is a different operation with different costs." A tenant with more than 200
 * trips running at once is in a state this screen cannot help with, and the honest thing then is to
 * say the board is partial.
 */
const PER_FETCH = "200";

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: RideOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface ActiveRidesProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /**
   * What this operator holds, which decides whether the cancel control acts or explains.
   *
   * Optional for the same reason the other boards' is: a caller that does not know the scopes
   * should get the control drawn and live rather than hidden, because hiding a control is how a
   * permission problem becomes a missing-feature report. Passing an empty set is a viewer.
   */
  readonly scopes?: ReadonlySet<string>;
}

interface Loaded {
  readonly rows: readonly Trip[];
  /** TRP-01 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

export function ActiveRides({ transport, tenantId, crumbs, scopes }: ActiveRidesProps): ReactNode {
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelFailure, setCancelFailure] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [selectedTripId, setSelectedTripId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<TripStatus | undefined>(undefined);
  const [order, setOrder] = useState<RideOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = trips.createTripClient(transport);
    try {
      const answered = await client.getTenantsTrips(tenantId, status, PER_FETCH, undefined);
      setLoaded({
        rows: answered.trips,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      // One state rather than a banner over a half-rendered table. A board showing two of seven
      // trips with no explanation is worse than one showing none.
      setFailure(stateForError(error, ["trip:read", "trip:workload:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const view = useMemo(
    () => ridePage(loaded?.rows ?? [], { search, order, page, perPage }),
    [loaded, search, order, page, perPage],
  );

  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedTrip = loaded?.rows.find((trip) => trip.tripId === selectedTripId);

  /*
   * No `observedAt`, so no stale state.
   *
   * The demand queue can say how far behind it is, because DSP-05 is a projection and publishes its
   * own watermark. TRP-01 is the trip's system of record: `updatedAt` is per row and there is no
   * page-level instant, so this screen cannot tell an operator that what they are looking at is a
   * minute old. Timing it from the moment the browser asked would call the data fresh whenever the
   * request succeeded, which is the one thing a stale banner must not do. R-19.
   */
  const state = failure ?? stateForList(loaded?.rows);

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded?.truncated ?? false,
    status,
    Number(PER_FETCH),
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="rides__head">
      <span>Trip</span>
      <span>Rider</span>
      <span className="rides__head--right">State</span>
    </div>
  );

  return (
    <section className="page rides">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Trips"
        // The design says "TRP-01…04". This screen reads TRP-01 and nothing else: TRP-02's tracking
        // is the live map's, TRP-03 answers one scheduled ride by identifier and TRP-04 one stop
        // plan, and neither answers a listing this board could join. A screen naming a service it
        // does not read is worse than one naming nothing. R-19.
        source="TRP-01"
        description="All trips in progress and today's finished ones."
        action={
          <button
            type="button"
            className="rides__primary"
            /*
             * Drawn, and refused — the same treatment R-08 gave the demand queue's `+ Manual
             * assign` and R-09 gave the live map's seven card buttons, for the same reason.
             *
             * The design's form is "Book a trip for a rider" over a rider's phone and name, a
             * pickup, a drop-off, a service and a when. `POST /v1/tenants/{tenantId}/trips` takes
             * none of those: its `CreateTrip` is `riderId`, `driverId`, `assignmentId`,
             * `assignmentVersion`, `requestId`, `requestVersion`, `quoteId`, `quoteVersion` and
             * `occurredAt` — a trip created *from* an assignment that already exists, under
             * `trip:workload:create`, which `navigation.ts` does not grant this page. A phone
             * booking is a ride request, a quote, a dispatch and then a trip, and no operation on
             * this platform starts that chain from an address. R-19.
             */
            disabled
            title="TRP-01 creates a trip from an assignment that already exists, not from an address. No operation books a ride for a rider. See R-19."
          >
            <span aria-hidden="true" className="rides__primary-glyph">
              add
            </span>
            Book for a rider
          </button>
        }
      />

      <div className="rides__tiles">
        {/*
          One tile, where the design has four.

          "In progress" is a count of the running trips this screen holds, which TRP-01 can answer
          because its list is live by default. "Completed today" and "Cancelled · today" need a
          date-bounded total, and TRP-01's listing takes `status`, `limit` and `after` and no date
          at all — nor does any listing on this platform publish a total (R-04). "Disputed" is not a
          value of any trip status anywhere. None of the three has a source, so they are absent
          rather than estimated. R-19.
        */}
        <div className="rides__tile">
          <span className="rides__tile-label">In progress</span>
          <span className="rides__tile-count">{loaded?.rows.length ?? 0}</span>
          {/* The design's own badge for this tile has no sub-label, and its `badge()` draws `—`
              for an absent one. Copied rather than improved: the glyph and hue still carry the
              class, so the state is not shown by colour alone. */}
          <StateBadge code="TRP-01" status="live" classKey="active" label="—" />
        </div>
      </div>

      {/*
        The grid and its panel in one positioned container, which is what docks the panel.

        `.detail` is docked two ways and both are written against `.board-split`: at 1240 and
        above `.board-split:has(> .detail)` makes it a 420px second column, and below 1240 the
        panel is `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s
        own `position: relative`. This board had no such wrapper, so an opened row drew a
        full-width block stacked under the table at 1440, and below 1240 an overlay anchored to
        the page rather than to the board. `DemandQueue.tsx` carries the measurements that found
        it: the first time any of these six panels was photographed, on 1 November.

        Which docking is right for these six at all is Q-297 — `19 WP18 Dispatch Operations` draws
        a centred modal where `04 Dispatch Exceptions Board` draws this column, and that is a
        decision rather than a defect. The wrapper is not that decision: it is what makes the
        build do what its own stylesheet already says.
      */}
      <div className="board-split">
        <div className="rides__panel">
          <div className="rides__toolbar">
            <span className="rides__search">
              <span aria-hidden="true" className="rides__search-glyph">
                search
              </span>
              <input
                id="active-rides-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="active-rides-status"
              aria-label="Filter by state"
              value={status ?? ""}
              onChange={(event) => {
                setStatus(
                  event.target.value === "" ? undefined : (event.target.value as TripStatus),
                );
                setPage(1);
              }}
            >
              {/* The design's word for the unfiltered option, over TRP-01's own eight rather than
                over the states present in the rows — see the module's FILTERABLE_STATUSES. */}
              <option value="">All states</option>
              {FILTERABLE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {readableStatus(value)}
                </option>
              ))}
            </select>
            <select
              id="active-rides-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as RideOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="rides__export" ref={exportRegion}>
              <button
                type="button"
                className="rides__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="rides__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="rides__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="rides__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format);
                      }}
                    >
                      <span aria-hidden="true" className="rides__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Trips appear here as soon as a driver is assigned to one."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 3,
              rowClassName: "rides__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="rides__rows">
              {view.rows.map((trip) => {
                const tint = rowTint(trip.status);
                return (
                  <div
                    key={trip.tripId}
                    className={tint === undefined ? "rides__row" : `rides__row ${tint}`}
                    data-selected={trip.tripId === selectedTripId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={trip.tripId === selectedTripId}
                    aria-label={`Open trip ${trip.tripId}`}
                    onClick={() => setSelectedTripId(trip.tripId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedTripId(trip.tripId);
                      }
                    }}
                  >
                    <span className="rides__trip">{trip.tripId}</span>
                    <span className="rides__rider">{riderCell(trip)}</span>
                    <span className="rides__state">
                      <StateBadge
                        code="TRP-01"
                        status={trip.status}
                        label={readableStatus(trip.status)}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="rides__no-match">
                <span aria-hidden="true" className="rides__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="rides__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="rides__pager">
              <span>
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="rides__pager-controls">
                <select
                  id="active-rides-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="rides__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {/*
          What this board is actually showing, said rather than hidden.

          The same line the demand queue and the offer monitor draw, for the reason R-08 defends. It
          matters more here than on either, because the page's own description — the design's words,
          kept word for word — promises "today's finished ones" and TRP-01 cannot be asked for a
          day: its listing takes one status, a limit and a cursor, and no date.
        */}
          {bounded === undefined ? null : (
            <p className="rides__bounded" role="status">
              {bounded}
            </p>
          )}
        </div>

        {/* The selected trip, found across everything fetched rather than the visible page. */}
        {selectedTrip === undefined ? null : (
          <RecordDetail
            record={rideDetail(selectedTrip)}
            open
            onClose={() => {
              setSelectedTripId(undefined);
              setCancelling(false);
            }}
            action={
              /*
               * The one trip-moving action the design draws, and its own note is the whole
               * specification: *"Cancelling a trip for the rider asks for a reason and shows the fee
               * before you confirm."* No `Mark arrived` and no `Start trip` appear anywhere in the
               * design, so this board commands one transition and no other.
               */
              <CancelTrip
                trip={selectedTrip}
                scopes={scopes}
                open={cancelling}
                busy={busy}
                reason={reason}
                failure={cancelFailure}
                onOpen={() => {
                  setCancelling(true);
                  setCancelFailure(undefined);
                }}
                onReason={setReason}
                onDismiss={() => {
                  setCancelling(false);
                  setReason("");
                  setCancelFailure(undefined);
                }}
                onConfirm={() => {
                  setBusy(true);
                  setCancelFailure(undefined);
                  cancelTrip(transport, {
                    tenantId,
                    trip: selectedTrip,
                    reasonCode: reason.trim(),
                  })
                    .then(() => {
                      setCancelling(false);
                      setReason("");
                      /*
                       * Reloaded rather than patched into the row in place. A cancelled trip leaves
                       * the live set, so the board's own filter decides whether it is still on the
                       * page — and a row edited locally would stay visible under a filter that no
                       * longer matches it, which reads as a cancel that did not take.
                       */
                      void load();
                    })
                    .catch((error: unknown) => {
                      setCancelFailure(
                        error instanceof Error ? error.message : "TRP-01 refused the cancellation.",
                      );
                    })
                    .finally(() => {
                      setBusy(false);
                    });
                }}
              />
            }
          />
        )}
      </div>

      <div className="rides__note">
        <span aria-hidden="true" className="rides__note-glyph">
          info
        </span>
        <span>
          Cancelling a trip for the rider asks for a reason and shows the fee before you confirm.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * enum. Excel takes an HTML table, which is what `application/vnd.ms-excel` has always meant in
 * practice, and PDF is the browser's own print dialogue. The same three the design's own grid
 * writes, from the same rows.
 */
function exportRows(rows: readonly Trip[], format: ExportFormat): void {
  const header = ["Trip", "Rider", "State"];
  const body = rows.map((trip) => [trip.tripId, riderCell(trip), readableStatus(trip.status)]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "trips.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "trips.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>trips</title></head><body><h2 style="font-family:sans-serif">trips</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}

interface CancelTripProps {
  readonly trip: Trip;
  readonly scopes: ReadonlySet<string> | undefined;
  readonly open: boolean;
  readonly busy: boolean;
  readonly reason: string;
  readonly failure: string | undefined;
  readonly onOpen: () => void;
  readonly onReason: (value: string) => void;
  readonly onDismiss: () => void;
  readonly onConfirm: () => void;
}

/**
 * Cancelling a trip for the rider: the design's one trip-moving action, and its confirmation.
 *
 * The design's note on this board is the specification — *"Cancelling a trip for the rider asks for
 * a reason and shows the fee before you confirm."* Three things follow from it, and the third is
 * the one worth reading.
 *
 * **It asks for a reason**, because TRP-01 requires `reasonCode` on a cancellation and refuses the
 * transition without one. The control stays disabled until there is one, rather than sending an
 * empty string and letting the service say no.
 *
 * **It confirms**, rather than cancelling on the first press. A cancellation cannot be undone —
 * TRP-01's transition table has no path out of `cancelled` — so the press that does it is the
 * second one.
 *
 * **And it cannot show the fee.** Nothing on this platform publishes a cancellation fee, under that
 * or any other name, so the line the design draws is drawn as an absence with the reason on it. A
 * number here would have had to be invented, and an invented fee shown before a confirmation is
 * worse than a missing one: an operator would read it to the rider.
 */
function CancelTrip({
  trip,
  scopes,
  open,
  busy,
  reason,
  failure,
  onOpen,
  onReason,
  onDismiss,
  onConfirm,
}: CancelTripProps): ReactNode {
  /*
   * Undefined scopes means a caller that does not know, and that is drawn as able to act. Hiding a
   * control from somebody who holds the scope is how a permission question becomes a bug report
   * about a missing feature; drawing it and being refused by the service is legible.
   */
  const viewOnly = scopes !== undefined && !scopes.has(CANCEL_SCOPE);
  const terminal = !cancellable(trip.status);

  if (terminal) {
    return (
      <p className="rides__cancel-note">
        {`This trip is ${readableStatus(trip.status).toLowerCase()}, so TRP-01 accepts no further transitions on it.`}
      </p>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        className="rides__cancel"
        disabled={viewOnly}
        title={
          viewOnly
            ? `Needs ${CANCEL_SCOPE}. Your role can view only.`
            : "Cancels this trip for the rider. Asks for a reason first."
        }
        onClick={onOpen}
      >
        <span aria-hidden="true" className="rides__cancel-glyph">
          cancel
        </span>
        Cancel trip
      </button>
    );
  }

  return (
    <div className="rides__confirm">
      <p className="rides__confirm-title">Cancel this trip for the rider?</p>

      {/*
        The fee the design asks for, drawn as what it is. Nothing publishes a cancellation fee:
        TRP-01 carries the trip's own fare and no charge for calling it off, and no other service
        names one. Shown rather than omitted, because an operator reading this screen to a rider
        needs to know the platform cannot tell them the fee, not merely that it did not say.
      */}
      <p
        className="rides__confirm-absent"
        title="No operation on this platform publishes a cancellation fee. TRP-01 carries the trip's fare and no charge for cancelling it."
      >
        Fee — not published
      </p>

      <label className="rides__confirm-label" htmlFor="rides-cancel-reason">
        Reason
      </label>
      <textarea
        id="rides-cancel-reason"
        className="rides__confirm-reason"
        value={reason}
        rows={2}
        disabled={busy}
        onChange={(event) => onReason(event.target.value)}
      />

      {failure === undefined ? null : <p className="rides__confirm-failure">{failure}</p>}

      <div className="rides__confirm-buttons">
        <button type="button" className="rides__confirm-keep" disabled={busy} onClick={onDismiss}>
          Keep the trip
        </button>
        <button
          type="button"
          className="rides__confirm-go"
          disabled={busy || reason.trim().length === 0}
          title={
            reason.trim().length === 0
              ? "TRP-01 refuses a cancellation without a reason."
              : "Cancels the trip. This cannot be undone."
          }
          onClick={onConfirm}
        >
          {busy ? "Cancelling…" : "Cancel the trip"}
        </button>
      </div>
    </div>
  );
}
